import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import AppFilterGrid, { AppFilterResetButton } from '../layout/AppFilterGrid';
import FamilleAutoC from '../AutoComplete/FamilleAutoC';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import { FamilleData, Tiers } from '../../models/models';
import { GeneriqueDataType } from '../../constants/enums';
import { useSelector } from 'react-redux';
import { RootState } from '../../reduxStore/Store';
import SelectFiltrePartage from '../Selects/SelectFiltrePartage';
import { SANS_FAMILLE, SANS_FOURNISSEUR } from '../../constants/filtres';
import { labels } from '../../constants/labels';

export interface ArticleFilterValues {
  code: string | null;
  designation: string | null;
  famille_id: number | null;
  fournisseur_id: number | null;   // 0143 — null = tous, 0 = sans fournisseur
  visible: 0 | 1 | null;
  partage: number[];   // filtre « Partage » (0132) — mode « Tous départements » uniquement
}

interface ArticleFilterProps {
  values: ArticleFilterValues;
  onChange: (values: ArticleFilterValues) => void;
  onReset: () => void;
}

const DEBOUNCE_MS = 400;

const ArticleFilter: React.FC<ArticleFilterProps> = ({ values, onChange, onReset }) => {
  const [resetKey, setResetKey] = React.useState(0);
  // Clés dédiées : « Sans famille » / « Sans fournisseur » ne vident que leur autocomplete
  const [familleKey, setFamilleKey] = React.useState(0);
  const [fournisseurKey, setFournisseurKey] = React.useState(0);
  const isAllMode = useSelector((state: RootState) => state.AppSlice.isAllMode);

  // Champs texte avec état local pour l'affichage immédiat + debounce pour l'appel API
  const [codeInput, setCodeInput]           = useState(values.code ?? '');
  const [designationInput, setDesignationInput] = useState(values.designation ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerChange = (field: keyof ArticleFilterValues, value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...values, [field]: value || null });
    }, DEBOUNCE_MS);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCodeInput(e.target.value);
    triggerChange('code', e.target.value);
  };

  const handleDesignationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDesignationInput(e.target.value);
    triggerChange('designation', e.target.value);
  };

  // Sync état local si reset externe
  useEffect(() => {
    setCodeInput(values.code ?? '');
    setDesignationInput(values.designation ?? '');
  }, [values.code, values.designation]);

  const handleReset = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setResetKey(k => k + 1);
    onReset();
  };

  const handleFamilleChange = (famille: FamilleData | null) =>
    onChange({ ...values, famille_id: famille?.id ?? null });

  const handleFournisseurChange = (fournisseur: Tiers | null) =>
    onChange({ ...values, fournisseur_id: fournisseur?.id ?? null });

  return (
    <AppFilterGrid>
      <TextField
        label="Code"
        size="small"
        fullWidth
        value={codeInput}
        onChange={handleCodeChange}
      />

      <TextField
        label="Désignation"
        size="small"
        fullWidth
        value={designationInput}
        onChange={handleDesignationChange}
      />

      <FamilleAutoC
        key={`famille-${resetKey}-${familleKey}`}
        slot="main"
        type={GeneriqueDataType.FAMILLE_ARTICLE}
        onFamilleChange={handleFamilleChange}
        disabled={values.famille_id === SANS_FAMILLE}
      />

      {/* famille_id = 0 → articles sans famille (migration 0133) */}
      <FormControlLabel
        label="Sans famille"
        control={
          <Checkbox
            size="small"
            checked={values.famille_id === SANS_FAMILLE}
            onChange={(e) => {
              setFamilleKey(k => k + 1); // vide l'autocomplete famille (sans toucher au fournisseur)
              onChange({ ...values, famille_id: e.target.checked ? SANS_FAMILLE : null });
            }}
          />
        }
      />

      <FournisseurAutoC
        key={`fournisseur-${resetKey}-${fournisseurKey}`}
        onFournisseurChange={handleFournisseurChange}
        disabled={values.fournisseur_id === SANS_FOURNISSEUR}
      />

      {/* fournisseur_id = 0 → articles sans fournisseur (migration 0143) */}
      <FormControlLabel
        label={labels.sansFournisseur}
        control={
          <Checkbox
            size="small"
            checked={values.fournisseur_id === SANS_FOURNISSEUR}
            onChange={(e) => {
              setFournisseurKey(k => k + 1); // vide l'autocomplete fournisseur (sans toucher à la famille)
              onChange({ ...values, fournisseur_id: e.target.checked ? SANS_FOURNISSEUR : null });
            }}
          />
        }
      />

      <FormControl fullWidth size="small">
        <InputLabel>Visible</InputLabel>
        <Select
          label="Visible"
          value={values.visible ?? ''}
          onChange={e => {
            const v = e.target.value;
            onChange({ ...values, visible: v === '' ? null : (Number(v) as 0 | 1) });
          }}
        >
          <MenuItem value="">Tous</MenuItem>
          <MenuItem value={1}>Oui</MenuItem>
          <MenuItem value={0}>Non</MenuItem>
        </Select>
      </FormControl>

      {isAllMode && (
        <SelectFiltrePartage
          value={values.partage}
          onChange={(partage) => onChange({ ...values, partage })}
        />
      )}

      <AppFilterResetButton onClick={handleReset} />
    </AppFilterGrid>
  );
};

export default ArticleFilter;
